import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { googlecode } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ColorPicker from './ColorPicker';
import PathEditor from './PathEditor';
import MetadataEditor from './MetadataEditor';

// Register XML language for syntax highlighting
SyntaxHighlighter.registerLanguage('xml', xml);

const PanelContainer = styled.div`
  flex: 1;
  background-color: #f0f0f0;
  border-left: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`;

const CodeHeader = styled.div`
  padding: 8px 10px;
  background-color: #e8e8e8;
  border-bottom: 1px solid #ccc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #e0e0e0;
  border-bottom: 1px solid #ccc;
  height: 32px;
  flex-shrink: 0;
`;

const Tab = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  background-color: ${props => props.active ? '#ffffff' : '#e0e0e0'};
  border-right: 1px solid #ccc;
  border-bottom: ${props => props.active ? 'none' : '1px solid #ccc'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  font-size: 13px;
  color: #333;
  height: 100%;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.active ? '#ffffff' : '#d6d6d6'};
  }
`;

const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

// This is the ONLY scrollable container
const CodeEditorContainer = styled.div`
  flex: 1;
  position: relative;
  background-color: #ffffff;
  overflow-y: auto;
  overflow-x: auto;
`;

// Fix the size issues with absolute positioning
const CodeEditorContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: 100%;
  display: flex;
`;

const CodeTextarea = styled.textarea`
  width: 100%;
  min-height: 100%;
  padding: 10px;
  border: none;
  outline: none;
  resize: none;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  color: transparent;
  background: transparent;
  caret-color: black;
  z-index: 2;
  tab-size: 2;
  white-space: ${props => props.wrap ? 'pre-wrap' : 'pre'};
  overflow: hidden;
`;

const SyntaxHighlighterWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  
  & pre {
    margin: 0 !important;
    background: transparent !important;
    padding: 10px !important;
    font-family: 'Consolas', 'Courier New', monospace !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
    tab-size: 2;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Button = styled.button`
  padding: 4px 10px;
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background-color: #e0e0e0;
  }
  
  &:active {
    background-color: #d0d0d0;
  }
  
  &:disabled {
    background-color: #f0f0f0;
    color: #999;
    cursor: not-allowed;
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 10px;
  background-color: #ffffff;
`;

const WrapToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-right: 10px;
  cursor: pointer;
  font-size: 12px;
  user-select: none;
`;

const StatusBar = styled.div`
  height: 22px;
  background-color: #f0f0f0;
  border-top: 1px solid #ddd;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  flex-shrink: 0;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
`;

const CodePanel = ({ svgCode, setSvgCode }) => {
  const [localCode, setLocalCode] = useState(svgCode);
  const [activeTab, setActiveTab] = useState('code');
  const [isValid, setIsValid] = useState(true);
  const [wrapText, setWrapText] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });
  const textareaRef = useRef(null);
  const editorContainerRef = useRef(null);
  
  useEffect(() => {
    setLocalCode(svgCode);
  }, [svgCode]);
  
  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    setLocalCode(newCode);
    
    // Check if the code is valid SVG
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(newCode, 'image/svg+xml');
      const errorNode = doc.querySelector('parsererror');
      setIsValid(!errorNode);
    } catch (e) {
      setIsValid(false);
    }
    
    // Update cursor position
    updateCursorPosition();
  };
  
  const updateCursorPosition = () => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const codeUpToCursor = textarea.value.substring(0, cursorPos);
    const lines = codeUpToCursor.split('\n');
    const line = lines.length;
    const column = lines[lines.length - 1].length + 1;
    
    setCursorPosition({ line, column });
  };
  
  const handleApplyChanges = () => {
    if (isValid) {
      setSvgCode(localCode);
    } else {
      alert('The SVG code contains errors. Please fix them before applying changes.');
    }
  };
  
  const handleFormatCode = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(localCode, 'image/svg+xml');
      
      // Check if parsing was successful
      const errorNode = doc.querySelector('parsererror');
      if (errorNode) {
        alert('Cannot format invalid SVG code. Please fix the errors first.');
        return;
      }
      
      // Format the code with proper indentation
      const serializer = new XMLSerializer();
      let formattedCode = serializer.serializeToString(doc);
      
      // Use a more sophisticated formatting approach
      formattedCode = formatXml(formattedCode);
      
      setLocalCode(formattedCode);
    } catch (e) {
      alert('Error formatting code: ' + e.message);
    }
  };
  
  // Helper function to format XML with proper indentation
  const formatXml = (xml) => {
    let formatted = '';
    let indent = '';
    const tab = '  '; // 2 spaces
    
    xml.split(/>\s*</).forEach(function(node) {
      if (node.match(/^\/\w/)) {
        // Closing tag
        indent = indent.substring(tab.length);
      }
      
      formatted += indent + '<' + node + '>\n';
      
      if (node.match(/^<?\w[^>]*[^\/]$/) && !node.startsWith("?")) {
        // Opening tag
        indent += tab;
      }
    });
    
    // Remove the first < and the last >
    return formatted.substring(1, formatted.length - 2);
  };
  
  // Handle tab key in textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      
      // Insert tab at cursor position
      const newText = localCode.substring(0, start) + '  ' + localCode.substring(end);
      setLocalCode(newText);
      
      // Move cursor after the inserted tab
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
        updateCursorPosition();
      }, 0);
    }
  };
  
  // Handle cursor movement
  const handleCursorMove = () => {
    updateCursorPosition();
  };
  
  // Count lines and characters
  const getCodeStats = () => {
    const lines = localCode.split('\n').length;
    const chars = localCode.length;
    return { lines, chars };
  };
  
  const stats = getCodeStats();
  
  return (
    <PanelContainer>
      <CodeHeader>
        <HeaderTitle>SVG Code Editor</HeaderTitle>
        <ButtonGroup>
          <WrapToggle>
            <input 
              type="checkbox" 
              checked={wrapText} 
              onChange={() => setWrapText(!wrapText)} 
              id="wrap-toggle"
            />
            <span>Wrap Text</span>
          </WrapToggle>
          <Button onClick={handleApplyChanges} disabled={!isValid}>
            Apply Changes
          </Button>
          <Button onClick={handleFormatCode}>
            Format Code
          </Button>
        </ButtonGroup>
      </CodeHeader>
      
      <TabContainer>
        <Tab 
          active={activeTab === 'code'} 
          onClick={() => setActiveTab('code')}
        >
          Code
        </Tab>
        <Tab 
          active={activeTab === 'colors'} 
          onClick={() => setActiveTab('colors')}
        >
          Colors
        </Tab>
        <Tab 
          active={activeTab === 'paths'} 
          onClick={() => setActiveTab('paths')}
        >
          Paths
        </Tab>
        <Tab 
          active={activeTab === 'metadata'} 
          onClick={() => setActiveTab('metadata')}
        >
          Metadata
        </Tab>
      </TabContainer>
      
      {activeTab === 'code' ? (
        <EditorWrapper>
          <CodeEditorContainer ref={editorContainerRef}>
            <CodeEditorContent>
              <CodeTextarea 
                ref={textareaRef}
                value={localCode} 
                onChange={handleCodeChange}
                onKeyDown={handleKeyDown}
                onKeyUp={handleCursorMove}
                onClick={handleCursorMove}
                spellCheck="false"
                wrap={wrapText}
              />
              <SyntaxHighlighterWrapper>
                <SyntaxHighlighter 
                  language="xml" 
                  style={googlecode}
                  wrapLines={true}
                  wrapLongLines={wrapText}
                  showLineNumbers={false}
                  customStyle={{ 
                    whiteSpace: wrapText ? 'pre-wrap' : 'pre',
                    background: '#ffffff',
                    minHeight: '100%'
                  }}
                >
                  {localCode}
                </SyntaxHighlighter>
              </SyntaxHighlighterWrapper>
            </CodeEditorContent>
          </CodeEditorContainer>
          <StatusBar>
            <StatusItem>
              {isValid ? 'Valid SVG' : 'Invalid SVG'}
            </StatusItem>
            <StatusItem>
              Ln {cursorPosition.line}, Col {cursorPosition.column}
            </StatusItem>
            <StatusItem>
              {stats.lines} lines, {stats.chars} characters
            </StatusItem>
          </StatusBar>
        </EditorWrapper>
      ) : activeTab === 'colors' ? (
        <TabContent>
          <ColorPicker svgCode={svgCode} setSvgCode={setSvgCode} />
        </TabContent>
      ) : activeTab === 'paths' ? (
        <TabContent>
          <PathEditor svgCode={svgCode} setSvgCode={setSvgCode} />
        </TabContent>
      ) : (
        <TabContent>
          <MetadataEditor svgCode={svgCode} setSvgCode={setSvgCode} />
        </TabContent>
      )}
    </PanelContainer>
  );
};

export default CodePanel; 